import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { css } from 'styled-components';
import { throttle } from 'throttle-debounce';

//components
import { TabsContainer, TabsList, TabNavItemHighlighter, TabContent, TabsHeader } from '../styles/cpds-tab-style';

//helpers
import { getChildrens } from '../helpers';
import { useLayoutDragHook, useScrolableContentHook } from '../hooks';

//types
import { CpdsTabProps } from '../typings';
import { Cpds } from '@Cpds.Ui';

const Tabs: React.FC<CpdsTabProps> = (props) => {
  const {
    exClasses = '',
    domRef,
    children,
    onTabSelect,
    selectedTab,
    forceScrollTabListInMobile = false,
    titleData = {},
    ...rest
  } = props;

  const { eyebrow, title, description } = titleData;

  const tabsRef = useRef<HTMLUListElement>(null);

  //natural width tab drag functionality
  const { mouseDown, mouseMove, mouseUp, scrollTabIntoView, scrollMouse } = useLayoutDragHook(tabsRef);

  //check whether natural width type is scrolable or not
  const { isScrollable, scrollWidth } = useScrolableContentHook(tabsRef);

  //use to animate highlighter
  const hightLighthterTiming = useRef<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  //handleClick
  const handleClick = useCallback(
    (index: number, e?: MouseEvent | KeyboardEvent) => {
      if (onTabSelect) {
        onTabSelect(index, e);
        scrollTabIntoView(e);
      }

      //prevent click while dragging the tabs
      if (scrollMouse.drag) {
        e.preventDefault();
        return;
      }
    },
    [onTabSelect, scrollTabIntoView, scrollMouse.drag],
  );

  const { navigationItems, tabContents } = useMemo(
    () => getChildrens(children, selectedTab, handleClick),
    [children, selectedTab, handleClick],
  );

  //scroll the selected tab to view on component mount
  // const scorllInitialTabIntoView = useCallback(() => {
  //   const tabEl = document.querySelector(`.cpds-tab-nav-item.selected`);
  //   if (tabEl) {
  //     tabEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  //   }
  // }, []);

  // useEffect(() => {
  //   scorllInitialTabIntoView()
  //   // Add event listener on mount
  //   window.addEventListener('resize', scorllInitialTabIntoView);

  //   // Cleanup event listener on unmount
  //   return () => {
  //     window.removeEventListener('resize', scorllInitialTabIntoView);
  //   };
  // }, []);

  const updateHighlighter = useCallback(() => {
    setIsTransitioning(true);

    hightLighthterTiming.current = window.setTimeout(() => {
      setIsTransitioning(false);
    }, 275);
  }, []);

  const throttledUpdateHighlighter = useMemo(() => throttle(100, false, updateHighlighter, false), [updateHighlighter]);

  const calculateOffset = useCallback((selectedTab: any) => {
    return selectedTab.offsetLeft;
  }, []);

  //componentDidMount and componetWillUnmount
  useEffect(() => {
    window.addEventListener('resize', throttledUpdateHighlighter);
    return () => {
      window.removeEventListener('resize', throttledUpdateHighlighter);
    };
  }, []);

  useEffect(() => {
    if (tabsRef.current && tabsRef.current.children.length > 0) {
      throttledUpdateHighlighter();
    }

    return () => {
      if (hightLighthterTiming.current) {
        clearTimeout(hightLighthterTiming.current);
        hightLighthterTiming.current = null;
      }
    };
  }, [throttledUpdateHighlighter]);

  useEffect(() => {
    throttledUpdateHighlighter();
  }, [selectedTab, throttledUpdateHighlighter]);

  let highlighterStyle = '';

  if (tabsRef.current && tabsRef.current.children.length > 0) {
    const ul = tabsRef.current;
    const listItem = ul.children[selectedTab || 0];
    if (listItem) {
      const selectedTabIndex = listItem.firstElementChild as HTMLElement;

      if (selectedTabIndex) {
        const highlighterWidth = selectedTabIndex.getBoundingClientRect().width;
        const highlighterOffset = calculateOffset(selectedTabIndex);
        const transformString = `translateX(${highlighterOffset}px) scaleY(${isTransitioning ? 2 : 1}) scaleX(1)`;

        //should -24px because of the padding between items
        highlighterStyle = css`
          transform: ${transformString};
          width: ${highlighterWidth ? `${highlighterWidth}px` : ''};
        `;
      }
    }
  }

  const tabList = () => {
    return (
      <TabsList
        ref={tabsRef}
        onMouseDown={mouseDown}
        onMouseUp={mouseUp}
        onMouseMove={mouseMove}
        onMouseLeave={mouseUp}
      >
        {navigationItems}
        <TabNavItemHighlighter className="tab-nav-highlighter" customStyles={highlighterStyle} />
      </TabsList>
    );
  };

  return (
    <TabsContainer
      className={`cpds-tab ${exClasses}`}
      ref={domRef}
      isScrollable={isScrollable}
      scrollWidth={scrollWidth}
      forceScrollTabListInMobile={forceScrollTabListInMobile}
      {...rest}
    >
      {eyebrow || title || description ? (
        <TabsHeader>
          <Cpds.SectionTitle
            eyebrowStyles={{ exClasses: 'cpds-body-roman-book-08', textAlign: 'left' }}
            titleStyles={{ exClasses: 'cpds-display-roman-book-03', textAlign: 'left' }}
            descriptionStyles={{ exClasses: 'cpds-body-roman-book-08', textAlign: 'left' }}
            bgColor="transparent"
            titleData={{
              eyebrow,
              title,
              description,
            }}
            styleOverrides={css`
              gap: 8px;
            `}
          />
          {tabList()}
        </TabsHeader>
      ) : (
        tabList()
      )}
      {tabContents && tabContents.length > 0 && <TabContent>{tabContents}</TabContent>}
    </TabsContainer>
  );
};

export { Tabs };
